/**
 * Environment Variable Security Validator
 * Ensures proper configuration and prevents security misconfigurations
 */

import { sanitizeErrorMessage } from './security';

/**
 * Validate critical environment variables for security
 */
export const validateEnvironmentSecurity = () => {
  const errors = [];
  const warnings = [];

  // Check for production-ready encryption key
  const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
  if (!encryptionKey) {
    errors.push('REACT_APP_ENCRYPTION_KEY is required');
  } else if (encryptionKey.includes('default') || encryptionKey.includes('dev') || encryptionKey.length < 32) {
    if (process.env.NODE_ENV === 'production') {
      errors.push('REACT_APP_ENCRYPTION_KEY must be a secure key in production (min 32 characters)');
    } else {
      warnings.push('Using development encryption key - ensure to change for production');
    }
  }

  // Validate network configuration
  const network = process.env.REACT_APP_SOLANA_NETWORK;
  if (process.env.NODE_ENV === 'production' && network !== 'MAINNET') {
    warnings.push('Consider using MAINNET for production deployment');
  }

  // Check for debug logs in production
  if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
    errors.push('Debug logs should be disabled in production');
  }

  // Validate program IDs format
  const programId = process.env.REACT_APP_CHIRPX_PROGRAM_ID;
  if (programId && !/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(programId)) {
    errors.push('REACT_APP_CHIRPX_PROGRAM_ID has invalid format');
  }

  // Check for unsafe eval in production
  if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_ALLOW_UNSAFE_EVAL === 'true') {
    warnings.push('ALLOW_UNSAFE_EVAL should be false in production unless required for wallet libraries');
  }

  // Validate rate limiting is enabled
  if (process.env.REACT_APP_ENABLE_RATE_LIMITING === 'false') {
    warnings.push('Rate limiting is disabled - this may allow abuse');
  }

  // Validate input sanitization is enabled
  if (process.env.REACT_APP_ENABLE_INPUT_SANITIZATION === 'false') {
    errors.push('Input sanitization should never be disabled');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    summary: {
      criticalIssues: errors.length,
      warnings: warnings.length,
      environment: process.env.NODE_ENV || 'development'
    }
  };
};

/**
 * Get safe environment configuration for logging
 * Excludes sensitive values
 */
export const getSafeEnvConfig = () => {
  return {
    nodeEnv: process.env.NODE_ENV,
    network: process.env.REACT_APP_SOLANA_NETWORK,
    debugLogs: process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true',
    rateLimiting: process.env.REACT_APP_ENABLE_RATE_LIMITING !== 'false',
    inputSanitization: process.env.REACT_APP_ENABLE_INPUT_SANITIZATION !== 'false',
    secureStorage: process.env.REACT_APP_ENABLE_SECURE_STORAGE !== 'false',
    walletValidation: process.env.REACT_APP_ENABLE_WALLET_VALIDATION !== 'false',
    httpsEnforced: process.env.REACT_APP_ENFORCE_HTTPS !== 'false',
    // Exclude sensitive values like keys, tokens, DSNs
  };
};

/**
 * Initialize environment validation on app startup
 */
export const initializeEnvironmentValidation = () => {
  try {
    const validation = validateEnvironmentSecurity();
    
    if (validation.errors.length > 0) {
      console.error('🚨 CRITICAL ENVIRONMENT SECURITY ISSUES:');
      validation.errors.forEach(error => console.error(`  ❌ ${error}`));
      
      if (process.env.NODE_ENV === 'production') {
        throw new Error('Critical environment security issues detected in production');
      }
    }

    if (validation.warnings.length > 0) {
      console.warn('⚠️ ENVIRONMENT SECURITY WARNINGS:');
      validation.warnings.forEach(warning => console.warn(`  ⚠️ ${warning}`));
    }

    if (validation.errors.length === 0 && validation.warnings.length === 0) {
      console.log('✅ Environment security validation passed');
    }

    // Log safe configuration in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Environment Configuration:', getSafeEnvConfig());
    }

    return validation;
  } catch (error) {
    console.error('❌ Environment validation failed:', sanitizeErrorMessage(error));
    throw error;
  }
};

export default {
  validateEnvironmentSecurity,
  getSafeEnvConfig,
  initializeEnvironmentValidation
};
